import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import {
  Button,
  Col,
  Form,
  FormProps,
  Input,
  ModalProps,
  notification,
  Row,
  Spin,
  Typography
} from "antd";
import { Rule } from "antd/es/form";
import isEqual from "lodash.isequal";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AutoComplete } from "../../../../components/inputs/autocomplete";
import { SelectField } from "../../../../components/inputs/select/select";
import { getOnboardingStatusOptionLabel, getWorldcheckVerificationStatusOptionLabel } from "../../../../components/inputs/select/statusSelect.constants";
import { addressRules, nameRules } from "../../../../components/modal/admin/const/rules";
import { buttonStyle } from "../../../../components/pages/auth/components/styles";
import { User } from "../../../../interfaces/users";
import { confirmModalUnsavedChangesProps } from "../../../../pages/components/constants";
import { companyRegistrationNumberRules } from "../../../../pages/payment-request/addCompany.constants";
import { fetchFurtherInformation } from "../../../../utils/fetchFurtherInformation";
import {
  clearFieldErrorMessageOnChange,
  isUserIdAvailableApi,
  onlyLatinCharsDigitsSpecialSymbolsRule,
  trimFormFieldValue,
  websiteRules,
} from "../../../../utils/form.constants";
import { getStorageUrl, useCustomer } from "../../../../utils/hooks/useCustomer.hook";
import { CopyButton } from "../../../components/copyButton";
import { useIsAdminRole } from "../../../../utils/hooks/useIdentityAdmin.hook";
import { useNotificationOkCancel } from "../../../../utils/hooks/useNotificationOkCancel.hook";
import { useUpdateCustomer } from "../../../../utils/hooks/useUpdateCustomer.hook";
import { OnboardingStatus, VerificationStatus } from "../../../../utils/hooks/useWorldCheckVerification.hook";

interface IProps {
  formProps?: FormProps;
  modalProps?: ModalProps;
  closeModal?: () => void;
  editingId?: number;
  changeEditingId?: (id: number | undefined) => void;
  refetchList?: () => void;
  show?: () => void;
  onUnsavedChangesCheck?: (checkUnsavedChanges: () => Promise<boolean>, resetForm: () => void) => void;
  isActive?: boolean;
}

export interface CustomerInformationRef {
  checkUnsavedChanges: () => boolean;
  resetForm: () => void;
}

export const CustomerInformation = ({
  formProps: externalFormProps,
  editingId,
  refetchList,
  onUnsavedChangesCheck,
  isActive = true,
}: IProps) => {
  const [form] = Form.useForm();
  const formProps = externalFormProps || { form };
  const emailValue = Form.useWatch("email", formProps.form);
  const { data: loadData, isLoading: loadIsLoading } = useCustomer(editingId, isActive);
  const { update, isLoading: updateIsLoading } = useUpdateCustomer(editingId);
  const [loadedFormValues, setLoadedFormValues] = useState<User | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState<boolean>(false);
  const [onboardingPdfUrl, setOnboardingPdfUrl] = useState<string | null>(null);
  const translate = useTranslate();
  const formIsLoading = loadIsLoading || updateIsLoading;
  const isAdminRole = useIsAdminRole();

  const {
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: () => {
      formProps?.form?.resetFields();
      setLoadedFormValues(null);
    },
  });

  const checkUnsavedChanges = useCallback(() => {
    if (!formProps?.form || !loadedFormValues) {
      return false;
    }
    return !isEqual(formProps.form.getFieldsValue(), loadedFormValues);
  }, [formProps?.form, loadedFormValues]);

  const resetForm = useCallback(() => {
    formProps?.form?.resetFields();
    if (loadData && formProps?.form) {
      formProps.form.setFieldsValue({
        fullName: loadData.fullName,
        email: loadData.email,
        smartUID: loadData.smartUID,
        address: loadData.address,
        website: loadData.website,
        worldCheckVerification: {
          status: loadData.worldCheckVerification.status,
          lastStatus: loadData.worldCheckVerification.lastStatus,
          regNo: loadData.regNo,
          companyCountry: loadData.country,
        },
        onboardingStatus: loadData.onboardingStatus,
      });
      setLoadedFormValues(formProps.form.getFieldsValue());
    }
  }, [formProps?.form, loadData]);

  useEffect(() => {
    if (loadData && formProps?.form) {
      formProps.form.setFieldsValue({
        fullName: loadData.fullName,
        email: loadData.email,
        smartUID: loadData.smartUID,
        address: loadData.address,
        website: loadData.website,
        worldCheckVerification: {
          status: loadData.worldCheckVerification.status,
          lastStatus: loadData.worldCheckVerification.lastStatus,
          regNo: loadData.regNo,
          companyCountry: loadData.country,
        },
        onboardingStatus: loadData.onboardingStatus,
      });
      setLoadedFormValues(formProps.form.getFieldsValue());
    }
  }, [loadData, formProps?.form]);

  useEffect(() => {
    if (loadData?.onboardingFormUuid) {
      getStorageUrl(loadData.onboardingFormUuid).then((url) => {
        setOnboardingPdfUrl(url);
      });
    } else {
      setOnboardingPdfUrl(null);
    }
  }, [loadData?.onboardingFormUuid]);

  useEffect(() => {
    if (onUnsavedChangesCheck) {
      onUnsavedChangesCheck(
        () => Promise.resolve(checkUnsavedChanges()),
        resetForm
      );
    }
  }, [onUnsavedChangesCheck, checkUnsavedChanges, resetForm]);

  const onSubmit = useCallback(
    async (formValues: User) => {
      const isSuccess = await update({
        fullName: formValues.fullName,
        address: formValues.address,
        smartUID: formValues.smartUID,
        website: formValues.website,
        worldCheckVerification: {
          status: formValues.worldCheckVerification.status,
        },
        onboardingStatus: formValues.onboardingStatus,
      });
      if (isSuccess) {
        if (refetchList) {
          refetchList();
        }
        setLoadedFormValues(formProps?.form?.getFieldsValue() || null);
        notification.success({
          message: "Customer updated successfully",
          placement: "bottom",
        });
      }
    },
    [refetchList, update, formProps?.form]
  );

  const userIdContainsSpaceValidator = useCallback(
    () => ({
      validator(_: any, value: string) {
        if (/\s/.test(value)) {
          return Promise.reject(new Error("SMART UID cannot contains spaces"));
        } else {
          return Promise.resolve();
        }
      },
      validateTrigger: "onSubmit",
    }),
    []
  );

  const userIdAvailableValidator = useCallback(
    () => ({
      async validator(_: any, value?: string) {
        if (
          value &&
          value.length >= 3 &&
          value.length <= 30 &&
          !/\s/.test(value) &&
          value !== loadData?.smartUID
        ) {
          const validationResult = await isUserIdAvailableApi(value);
          if (validationResult) {
            return Promise.resolve(true);
          } else {
            return Promise.reject("A user with this SMART UID already exists");
          }
        } else {
          return Promise.resolve(true);
        }
      },
      validateTrigger: "onSubmit",
    }),
    [loadData]
  );

  const emailRules: Rule[] = useMemo(
    () => [
      { required: true, validateTrigger: "onSubmit" },
      {
        type: "email",
        message: translate(
          "pages.register.errors.validEmail",
          "Invalid email address"
        ),
        validateTrigger: "onSubmit",
      },
    ],
    [translate]
  );

  async function handleFetchFurtherInformation() {
    await fetchFurtherInformation(
      loadData?.worldCheckVerification?.companyCaseId,
      loadData?.worldCheckVerification?.individualCaseId,
      setFetchingDetails
    );
  }

  const userIdRules: Rule[] = useMemo(
    () => [
      {
        required: true,
        message: "Please enter SMART UID",
        validateTrigger: "onSubmit",
      },
      {
        min: 3,
        message: translate(
          "pages.register.errors.minUserId",
          "Minimum 3 characters"
        ),
        validateTrigger: "onSubmit",
      },
      {
        max: 30,
        message: translate(
          "pages.register.errors.maxUserId",
          "Maximum 30 characters"
        ),
        validateTrigger: "onSubmit",
      },
    ],
    [translate]
  );

  if (editingId === undefined) {
    return null;
  }

  if (loadIsLoading) {
    return <Spin size="large" />;
  }

  return (
    <>
      {unsavedChangesContextHolder}
      <div style={{ padding: '16px', maxWidth: '100%', backgroundColor: '#fff' }}>
        <Form
          {...formProps}
          layout="vertical"
          onFinish={onSubmit}
          onValuesChange={clearFieldErrorMessageOnChange(formProps?.form)}
          disabled={formIsLoading}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Email" name="email" rules={emailRules}>
                <Input
                  size="large"
                  placeholder={translate("pages.register.fields.email", "Email")}
                  style={buttonStyle}
                  disabled
                  suffix={emailValue && <CopyButton value={emailValue} />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Full Name/ Company Name" name="fullName" rules={nameRules}>
                <Input placeholder="Full Name/ Company Name" style={buttonStyle} size="large" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Reg. No (TIN, VAT)" name={["worldCheckVerification", "regNo"]} rules={companyRegistrationNumberRules}>
                <Input placeholder="Reg. No (TIN, VAT)" style={{ ...buttonStyle }} size="large" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Country of incorporation"
                name={["worldCheckVerification", "companyCountry"]}
                rules={[
                  {
                    required: true,
                    message: "Country of incorporation is required",
                    validateTrigger: "onSubmit",
                  },
                ]}
              >
                <AutoComplete
                  resource="api/v1/worldCheck/countries"
                  mapItem={(item: { value: string; label: string }) => ({
                    value: item.label,
                    key: item.value,
                    label: <Typography.Text>{item.label}</Typography.Text>,
                    data: item,
                  })}
                  notFoundPlaceholder="Start by typing country name"
                  style={{ marginTop: '0px' }}
                >
                  <Input
                    style={{ height: '40px' }}
                    placeholder="Country of incorporation"
                    size="large"
                    suffix={<DownOutlined />}
                    disabled
                  />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="SMART UID"
                name="smartUID"
                rules={[
                  ...userIdRules,
                  onlyLatinCharsDigitsSpecialSymbolsRule,
                  userIdContainsSpaceValidator,
                  userIdAvailableValidator,
                ]}
              >
                <Input
                  disabled
                  size="large"
                  placeholder={translate(
                    "pages.register.fields.userId",
                    "SMART UID"
                  )}
                  style={buttonStyle}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Company's Address" name="address" rules={addressRules}>
                <Input placeholder="Address" style={buttonStyle} size="large" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Website" name="website" rules={websiteRules}>
                <Input
                  size="large"
                  placeholder={translate(
                    "pages.register.fields.website",
                    "Website"
                  )}
                  onBlur={trimFormFieldValue("website", formProps?.form)}
                  style={buttonStyle}
                />
              </Form.Item>
            </Col>
          </Row>
          {isAdminRole && (
            <>
              <Typography
                style={{ textAlign: "left", fontWeight: 500, fontSize: 16 }}
              >
                World Check verification status
              </Typography>
              <SelectField
                name={["worldCheckVerification", "status"]}
                getOptionLabel={getWorldcheckVerificationStatusOptionLabel}
                items={Object.values(VerificationStatus)}
              />
              {[
                VerificationStatus.HIGH_RISK_EDD,
                VerificationStatus.HIGH_RISK_FIR,
              ].includes(
                formProps?.form?.getFieldValue([
                  "worldCheckVerification",
                  "status",
                ])
              ) &&
                formProps?.form?.getFieldValue([
                  "worldCheckVerification",
                  "lastStatus",
                ]) === VerificationStatus.REQUIRED && (
                  <Typography style={{ textAlign: "left" }}>
                    <a onClick={handleFetchFurtherInformation}>See why</a>
                    {fetchingDetails && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{
                              fontSize: 16,
                              color: "blue",
                              marginLeft: "8px",
                            }}
                            spin
                          />
                        }
                      />
                    )}
                  </Typography>
                )}

              <Typography
                style={{ textAlign: "left", fontWeight: 500, fontSize: 16 }}
              >
                Onboarding Status
              </Typography>
              <SelectField
                name={["onboardingStatus"]}
                getOptionLabel={getOnboardingStatusOptionLabel}
                items={Object.values(OnboardingStatus)}
              // isOptionDisabled={(status: OnboardingStatus) =>
              //   status === OnboardingStatus.PENDING
              // }
              />
              {onboardingPdfUrl && (
                <Typography style={{ textAlign: "left", marginTop: 8 }}>
                  <a href={onboardingPdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
                    Download Onboarding PDF
                  </a>
                </Typography>
              )}
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button key='save' type='primary' htmlType="submit" loading={updateIsLoading} className='smartFooterButtonXS'>
              Save
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};
